// What the hedging desk's task for this enquiry says (ledger row 132, AC27,
// AC34). Phil's example, field for field: enquiry number, product, purchase
// pricing, sales pricing, quantity, delivery, "Action required: Paper trader
// review". The words are the server's (``TaskContent``); this window only
// lays them out, so the preview can never say something the task does not.

import React from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';
import './FloatingFormula.css';

// "Task with the hedging desk: Open" / "…: Done by Philotas Ghikas".
export const taskStatusText = (task) => {
  if (task.status === 'done') {
    return `Task with the hedging desk: Done${task.done_by_name ? ` by ${task.done_by_name}` : ''}`;
  }
  return `Task with the hedging desk: ${task.status_label || task.status}`;
};

const TaskContent = ({ preview }) => {
  const { task, exposed, content } = preview;
  const lines = Array.isArray(content.lines) ? content.lines : [];
  return (
    <>
      {task && <p className="ff-task-status">{taskStatusText(task)}</p>}
      {task?.changed_note && <p className="ff-task-changed">What changed: {task.changed_note}</p>}
      {!exposed && (
        <div className="ff-warning" role="note">
          As saved, the purchase and sale sides of this enquiry agree, so saving it
          would not send the hedging desk a task. Save your changes first to see the
          task they create.
        </div>
      )}
      <article className="ff-task-card">
        <h4 className="ff-task-title">{content.title}</h4>
        {content.customer_label && (
          <p className="ff-task-row">Customer: {content.customer_label}</p>
        )}
        {lines.map((line, idx) => (
          <div className="ff-task-line" key={`${line.label}-${idx}`}>
            {line.label && <div className="ff-task-line-label">{line.label}</div>}
            <p className="ff-task-row">Purchase: {line.purchase?.text}</p>
            <p className="ff-task-row">Sale: {line.sales?.text}</p>
            <p className="ff-task-row">Quantity: {line.quantity_text}</p>
            {Array.isArray(line.pricing_periods) && line.pricing_periods.length > 0 && (
              <p className="ff-task-row">Pricing period: {line.pricing_periods.join(', ')}</p>
            )}
          </div>
        ))}
        <p className="ff-task-row">Delivery: {content.delivery_text}</p>
        {lines.length > 1 && content.total_quantity_text && (
          <p className="ff-task-row">Total quantity: {content.total_quantity_text}</p>
        )}
        <p className="ff-task-action">Action required: {content.action}</p>
      </article>
      <p className="ff-muted">Shows the enquiry as last saved.</p>
    </>
  );
};

const HedgingTaskPreviewModal = ({ onClose, loading = false, error = null, preview = null }) => (
  createPortal(
    <div className="fef-modal-overlay" onClick={onClose}>
      <div
        className="fef-modal ff-task-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ff-task-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="fef-modal-header">
          <h3 className="fef-modal-title" id="ff-task-modal-title">Task for the hedging desk</h3>
          <button
            type="button"
            className="fef-modal-close"
            onClick={onClose}
            aria-label="Close the task preview"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>
        <div className="fef-modal-body ff-modal-body">
          {loading && !preview && (
            <p className="ff-muted"><FontAwesomeIcon icon={faSpinner} spin /> Loading the task…</p>
          )}
          {error && <div className="fef-modal-error" role="alert">{error}</div>}
          {preview && <TaskContent preview={preview} />}
        </div>
        <footer className="fef-modal-footer">
          <button type="button" className="fef-modal-btn fef-modal-btn-secondary" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  )
);

export default HedgingTaskPreviewModal;
